import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import * as Yup from "yup";
import { userTypeOptions } from "../../../constants";
import { updateUser } from "../../../store/slices/userSlice";
import regex from "../../../utils/regex";

const USER_FIELD_GETTERS = {
  firstName: (u) => u?.firstName,
  lastName: (u) => u?.lastName,
  userType: (u) => u?.userType,
  userEmail: (u) => u?.email?.address,
  userMobile: (u) => u?.phoneNumber?.number,
  crn: (u) => u?.crn,
  afcaNumber: (u) => u?.afcaNumber,
};

const getCurrentUserValue = (userState, fieldName) => {
  const getter = USER_FIELD_GETTERS[fieldName];
  return getter ? getter(userState) : userState?.[fieldName];
};

const initialUserFieldErrors = {
  firstName: "",
  lastName: "",
  userType: "",
  userMobile: "",
  crn: "",
  afcaNumber: "",
};

const userValidationSchema = Yup.object({
  firstName: Yup.string().matches(regex.name, "Only string allowed"),
  lastName: Yup.string().optional().matches(regex.name, { message: "Only string allowed", excludeEmptyString: true }),
  userType: Yup.string().optional().nullable().matches(regex.stringRegex, "Only string allowed"),
  userMobile: Yup.string()
    .optional()
    .matches(regex.phoneNumber, { message: "Please enter mobile number", excludeEmptyString: true }),
  crn: Yup.string()
    .optional()
    .matches(regex.allowOnlyNumber, { message: "Please enter credit representative number", excludeEmptyString: true }),
  afcaNumber: Yup.string()
    .optional()
    .matches(regex.allowOnlyNumber, { message: "Only numbers allowed", excludeEmptyString: true }),
});

const getInitialUserState = (userState, isShowTopLenders) => {
  if (!userState?._id) return null;
  const fromState = userState?.lenders ?? [];
  const lendersPadded = Array.isArray(fromState)
    ? (() => {
        const padded = [...fromState.slice(0, 6)];
        while (padded.length < 6) padded.push("");
        return padded;
      })()
    : ["", "", "", "", "", ""];
  const lendersInput = lendersPadded
    .map((s) => (s || "").trim())
    .filter(Boolean)
    .join(", ");
  return {
    firstName: userState?.firstName || "",
    lastName: userState?.lastName || "",
    userType: userState?.userType || "",
    userEmail: userState?.email?.address || "",
    userMobile: userState?.phoneNumber?.number || "",
    crn: userState?.crn || "",
    afcaNumber: userState?.afcaNumber || "",
    ...(isShowTopLenders ? { lenders: lendersPadded, lendersInput } : {}),
  };
};

const UserDetailsSection = ({
  userState,
  dispatch,
  userId,
  userSetting,
  onPaidByChange,
  isDisableInvoicePaidBy,
  serviceInvoicePaidByInfo,
  userAccessComponent,
}) => {
  const [state, setState] = useState(null);
  const [fieldErrors, setFieldErrors] = useState(initialUserFieldErrors);
  const isShowTopLenders = userAccessComponent.isShowTopLenders;

  useEffect(() => {
    setState(getInitialUserState(userState, isShowTopLenders));
  }, [userState, isShowTopLenders]);

  const validateField = async (fieldName, value) => {
    try {
      await userValidationSchema.validateAt(fieldName, { [fieldName]: value });
      setFieldErrors((prev) => ({ ...prev, [fieldName]: "" }));
    } catch (error) {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: error.message }));
    }
  };

  const handle = {
    onChangeField: (value, name) => {
      setState((prev) => (prev ? { ...prev, [name]: value } : prev));
      validateField(name, value);
    },
    onBlurField: (value, name) => {
      try {
        const oldVal = getCurrentUserValue(userState, name);
        if (oldVal === value) return;
        if (fieldErrors[name] !== "") return;

        setState((prev) => (prev ? { ...prev, [name]: value } : prev));

        if (!userId) return;
        dispatch(
          updateUser({
            userId,
            ...(name === "crn" ? { crn: value } : { [name]: value }),
          }),
        );
      } catch (error) {
        const newErrors = {};
        error?.inner?.forEach((err) => {
          newErrors[err.path] = err.message;
        });
        setFieldErrors((prev) => ({ ...prev, ...newErrors }));
      }
    },
    onBlurTopLenders: () => {
      const raw = (state?.lendersInput ?? "").trim();
      const lenders = raw
        ? raw
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
            .slice(0, 6)
        : [];
      const padded = [...lenders, ...Array(6 - lenders.length).fill("")];
      const normalizedInput = lenders.join(", ");
      setState((prev) => (prev ? { ...prev, lenders: padded, lendersInput: normalizedInput } : prev));
      const existing = userState?.lenders ?? userState?.top_lenders ?? [];
      const same =
        Array.isArray(existing) &&
        lenders.length === existing.length &&
        lenders.every((v, i) => v === (existing[i] ?? ""));
      if (!same && userId) {
        dispatch(updateUser({ userId, lenders }));
      }
    },
  };

  if (!state) return null;

  return (
    <>
      <Grid item xs={8}>
        <Typography variant="h4" style={{ margin: "0 0 5px 0" }}>
          User Details
        </Typography>
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="First name"
          variant="filled"
          type="text"
          value={state.firstName}
          error={!!fieldErrors.firstName}
          helperText={fieldErrors.firstName}
          onChange={(e) => handle.onChangeField(e.target.value, "firstName")}
          onBlur={(e) => handle.onBlurField(e.target.value, "firstName")}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="Last name"
          variant="filled"
          type="text"
          value={state.lastName}
          error={!!fieldErrors.lastName}
          helperText={fieldErrors.lastName}
          onChange={(e) => handle.onChangeField(e.target.value, "lastName")}
          onBlur={(e) => handle.onBlurField(e.target.value, "lastName")}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="Email address"
          variant="filled"
          type="text"
          value={state.userEmail}
          disabled
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="Mobile number"
          variant="filled"
          type="text"
          value={state.userMobile}
          error={!!fieldErrors.userMobile}
          helperText={fieldErrors.userMobile}
          onChange={(e) => handle.onChangeField(e.target.value, "userMobile")}
          onBlur={(e) => handle.onBlurField(e.target.value, "userMobile")}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="Credit Representative Number"
          variant="filled"
          type="text"
          value={state.crn}
          error={!!fieldErrors.crn}
          helperText={fieldErrors.crn}
          onChange={(e) => handle.onChangeField(e.target.value, "crn")}
          onBlur={(e) => handle.onBlurField(e.target.value, "crn")}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="Credit Representative AFCA Member Number"
          variant="filled"
          type="text"
          placeholder="0000000000"
          value={state.afcaNumber}
          error={!!fieldErrors.afcaNumber}
          helperText={fieldErrors.afcaNumber}
          onChange={(e) => handle.onChangeField(e.target.value, "afcaNumber")}
          onBlur={(e) => handle.onBlurField(e.target.value, "afcaNumber")}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <Autocomplete
          options={userTypeOptions}
          variant="filled"
          size="small"
          disabled
          fullWidth
          value={state.userType || ""}
          style={{ margin: "0 0 5px 0" }}
          renderInput={(params) => (
            <TextField
              size="small"
              {...params}
              label="User type"
              variant="filled"
              error={!!fieldErrors.userType}
              helperText={fieldErrors.userType}
            />
          )}
        />
      </Grid>

      {serviceInvoicePaidByInfo && (
        <Grid item xs={8} sx={{ marginBottom: "10px" }}>
          <Alert severity="info">{serviceInvoicePaidByInfo}</Alert>
        </Grid>
      )}

      <Grid item xs={8} sx={{ marginBottom: "10px" }}>
        <FormControl fullWidth>
          <InputLabel id="invoice-paid-by">Invoice paid by</InputLabel>
          <Select
            variant="filled"
            size="small"
            id="invoice-paid-by"
            value={userSetting || ""}
            label="Invoice paid by"
            onChange={onPaidByChange}
            disabled={isDisableInvoicePaidBy}
          >
            {["ORGANISATION", "USER"].map((item) => (
              <MenuItem value={item} key={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {userAccessComponent.isShowTopLenders && (
        <Grid item xs={8}>
          <Typography variant="body2" style={{ margin: "0 0 5px 0", fontWeight: "600" }}>
            Top 6 Lenders by Volume
          </Typography>
          <TextField
            label="Lenders"
            placeholder="e.g. ANZ, Azora, etc..."
            variant="filled"
            type="text"
            value={state.lendersInput ?? ""}
            onChange={(e) => setState((prev) => (prev ? { ...prev, lendersInput: e.target.value } : prev))}
            onBlur={handle.onBlurTopLenders}
            size="small"
            style={{ margin: "0 0 5px 0" }}
            fullWidth
            helperText="Enter up to 6 lenders separated by commas"
          />
        </Grid>
      )}
    </>
  );
};

export default UserDetailsSection;
